import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
    id: string
    email: string
    displayName: string
    avatarUrl: string | null
    role: string
    permissions: string[]
}

interface AuthState {
    user: User | null
    accessToken: string | null
    refreshToken: string | null
    isAuthenticated: boolean

    // Impersonation state
    originalUser: User | null
    originalAccessToken: string | null
    originalRefreshToken: string | null
    isImpersonating: boolean

    setAuth: (user: User, accessToken: string, refreshToken: string) => void
    clearAuth: () => void
    hasPermission: (permission: string) => boolean

    startImpersonation: (user: User, accessToken: string, refreshToken: string | null) => void
    stopImpersonation: () => void
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,

            originalUser: null,
            originalAccessToken: null,
            originalRefreshToken: null,
            isImpersonating: false,

            setAuth: (user, accessToken, refreshToken) =>
                // A fresh sign-in is never an impersonation session. Reset any impersonation
                // state too, so a session that expired mid-impersonation (which clears tokens
                // but not the impersonation flags) can't leave us "impersonating ourselves".
                set({
                    user, accessToken, refreshToken, isAuthenticated: true,
                    originalUser: null, originalAccessToken: null, originalRefreshToken: null,
                    isImpersonating: false
                }),

            clearAuth: () =>
                set({
                    user: null, accessToken: null, refreshToken: null, isAuthenticated: false,
                    originalUser: null, originalAccessToken: null, originalRefreshToken: null, isImpersonating: false
                }),

            hasPermission: (permission: string) => {
                const { user } = get()
                return user?.permissions.includes(permission) ?? false
            },

            startImpersonation: (targetUser, tokensAccess, tokensRefresh) => {
                const state = get()
                if (state.isImpersonating) return // Prevent nested impersonation

                set({
                    originalUser: state.user,
                    originalAccessToken: state.accessToken,
                    originalRefreshToken: state.refreshToken,
                    isImpersonating: true,

                    user: targetUser,
                    accessToken: tokensAccess,
                    refreshToken: tokensRefresh
                })
            },

            stopImpersonation: () => {
                const state = get()
                if (!state.isImpersonating) return

                set({
                    user: state.originalUser,
                    accessToken: state.originalAccessToken,
                    refreshToken: state.originalRefreshToken,

                    originalUser: null,
                    originalAccessToken: null,
                    originalRefreshToken: null,
                    isImpersonating: false
                })
            }
        }),
        {
            name: 'puresoft-auth',
            partialize: (state) => ({
                user: state.user,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                isAuthenticated: state.isAuthenticated,

                originalUser: state.originalUser,
                originalAccessToken: state.originalAccessToken,
                originalRefreshToken: state.originalRefreshToken,
                isImpersonating: state.isImpersonating,
            }),
            // Self-heal inconsistent persisted state: if we're flagged as impersonating but
            // there's no distinct original user to revert to (e.g. a session expired mid-
            // impersonation, then signed back in), drop the impersonation flags so we don't
            // appear to be impersonating ourselves.
            onRehydrateStorage: () => (state) => {
                if (state?.isImpersonating &&
                    (!state.originalUser || state.originalUser.id === state.user?.id)) {
                    state.originalUser = null
                    state.originalAccessToken = null
                    state.originalRefreshToken = null
                    state.isImpersonating = false
                }
            },
        }
    )
)
